import {batch} from 'react-redux';
import {isNil, uniq, isEmpty, orderBy} from 'lodash';
import {exitSimpleTransferCreationMode} from 'contents/ProtocolEditor/TaskList/Task/SimpleTransfer/Tasks/simpleTransferTaskBusiness';
import {getSelectedTasks} from 'stores/selectors/tasklistSelector';
import {setMultipleSelectedTasks} from 'stores/actions/taskListStateAction';
import {getCards} from 'stores/selectors/cardStateSelector';
import {setDefaultTipTouchOffDirectionTasks} from 'lib/PipettingProfileItemHelper';
import {hideModal, showModal} from 'stores/actions/modalStateAction';
import {validateMinVolumeAllowed} from 'lib/protocolHelper';
import {
  TaskType,
  TipChannelType,
  CardType,
  ViewMode,
  RuntimeMode,
  WellCount,
  NormalizationPreviewWellStatus,
  CardInvalidType,
  Feedback,
  TrayType,
  CommonButtonLabel,
  TipCaddyLocationType,
} from '../../constants/dataEnum';
import {DefaultPipettingParameterValue} from '../../constants/pipettingParameterEnum';
import * as TemporaryStateType from '../../constants/temporaryStateType';

import * as cardStateAction from '../actions/cardStateAction';
import * as appStateAction from '../actions/appStateAction';
import * as normalizationStateAction from '../actions/normalizationStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import * as protocolEditorAction from '../actions/protocolEditorAction';
// eslint-disable-next-line import/no-cycle
import * as protocolManagementBusiness from './protocolManagementBusiness';
import * as labwareStateAction from '../actions/labwareStateAction';
import {
  convertRowToAlphabet,
  generateCardIDAndTaskIDAndNumber,
  getNewCardName,
  getTipCount,
} from './commonTaskListBusiness';
import {
  find,
  filterToArray,
  bulkInsertAfterCertainID,
  lowercaseEveryKeysFirstWord,
  mapObject,
} from '../../lib/objectHelper';
import {globalAddUndoItem} from './undoRedoBusinessAction';
import jsonCopy from '../../lib/jsonCopy';
import {incrementName, parseMessage} from '../../lib/stringHelper';
import {resolveIntl} from './commonBusiness';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {setLastOpenedProtocol, setOpenedProtocolEjectToCaddy} from '../actions/protocolAction';
import {
  rearrangeCards,
  rearrangeCardObject,
  getFirstSelectedCard,
  getTasksInCard,
  groupTaskByNumber,
} from '../../lib/cardAndTaskHelper';
import {generateCardObject} from './protocolStateConversionBusiness';
import {CopyPipettingValueMode, TASKS_NEED_VALID_LABWARE} from '../../constants/cardStateEnum';
import {standardizeSdRawTasks} from './protocolMigrationBusiness';
import {
  extractPipettingParameterFromTask,
  getAppliedPipettingParameters,
  getRangeMixingSpeedPipettingParameter,
  getRangeSpeedPipettingParameter,
} from './pipettingParameterBusiness';
import {saveState} from './stateBusiness';

const intl = resolveIntl();

const convertRowToNumeric = (row) => parseInt(row.charCodeAt(0) - 64, 10);

// #region smarter default dispense tip position

export const getDefaultTipPositionByVolume = (volume) => (dispatch, getState) => {
  const {configState} = getState();
  const {smartTipLists, defaultTipPosition, isSmartTipPositionActive} = configState;

  let tipPosition = defaultTipPosition;

  if (!isSmartTipPositionActive) {
    return tipPosition;
  }

  if (volume === 0) {
    return tipPosition;
  }

  const smartTip = smartTipLists.find((val) => val.volume === volume);

  if (!smartTip) {
    const newVolume = parseFloat(parseFloat(volume - 0.1).toFixed(1));
    return dispatch(getDefaultTipPositionByVolume(newVolume));
  }

  tipPosition = smartTip.height;

  return tipPosition;
};

export const smarterDefaultDispenseTipPosition =
  (labwareIDs = []) =>
  (dispatch, getState) => {
    const {cardState, labwareState, configState} = getState();
    const {cards, tasks} = cardState;
    const {defaultTipPosition, smartTipLists, isSmartTipPositionActive} = configState;

    if (!isSmartTipPositionActive) {
      return;
    }

    const sortedTaskId = Object.values(cards).reduce((prev, curr) => [...prev, ...curr.tasks], []);
    const highestVolumeSmartTips = Math.max(...smartTipLists.map((val) => val.volume));

    const getNormalizeDispenseTasks = (labware) => {
      const tasklist = [];

      const minTipPosition = dispatch(
        protocolManagementBusiness.getMinTipPositionByLabwareId(labware.LabwareID, false, labware.labwareIndex),
      );

      // normalize dispense only on reguler labware
      if (minTipPosition === defaultTipPosition) {
        const isSkipWell = labware.isIFC || labware.colCount * labware.rowCount === WellCount.WELL_384;

        let dispenseTasks = Object.values(tasks).filter(
          (task) => task.type === TaskType.DISPENSE && task.labwareID === labware.LabwareID,
        );

        const dispenseTaskIds = dispenseTasks.map((task) => task.ID);
        const sortedDispenseTaskIds = sortedTaskId.filter((id) => dispenseTaskIds.includes(id));

        dispenseTasks = dispenseTasks.sort(
          (a, b) => sortedDispenseTaskIds.indexOf(a.ID) - sortedDispenseTaskIds.indexOf(b.ID),
        );

        const dispenseWellMaps = [];

        dispenseTasks.forEach((task) => {
          const {row, column, rowTipCount, columnTipCount, labwareIndex} = task;
          const newLabwareIndex = labwareIndex ?? 0;

          const wellMaps = [];

          for (let i = 0; i < rowTipCount; i += 1) {
            for (let j = 0; j < columnTipCount; j += 1) {
              wellMaps.push(
                `${convertRowToNumeric(row) + (isSkipWell ? 2 * i : i)}-${column + (isSkipWell ? 2 * j : j)}-${newLabwareIndex}`,
              );
            }
          }

          const isWetWell = wellMaps.every((val) => dispenseWellMaps.includes(val));

          const isSmartTipPosition = smartTipLists.find((val) => val.height === task.tipPosition);

          if ((isWetWell || task.volume > highestVolumeSmartTips) && isSmartTipPosition) {
            task.tipPosition = defaultTipPosition;
            tasklist.push(task);
          }

          if (!isWetWell && task.volume <= highestVolumeSmartTips) {
            const newTipPosition = dispatch(getDefaultTipPositionByVolume(task.volume));

            if (newTipPosition !== task.tipPosition) {
              task.tipPosition = newTipPosition;
              tasklist.push(task);
            }
          }

          if (!task?.isIgnored) dispenseWellMaps.push(...wellMaps);
        });
      }

      return tasklist;
    };

    const normalizeTasks = [];

    if (labwareIDs.length) {
      labwareIDs.forEach((labwareID) => {
        const labware = find(labwareState, {LabwareID: labwareID});
        if (labware) normalizeTasks.push(...getNormalizeDispenseTasks(labware));
      });
    } else {
      Object.values(labwareState).forEach((labware) => {
        normalizeTasks.push(...getNormalizeDispenseTasks(labware));
      });
    }

    if (normalizeTasks.length) {
      cardStateAction.batchRestoreCardTaskStateById(normalizeTasks);
    }
  };

// #endregion smarter default dispense tip position

/*
export const getTipAndMixingPositionFromTask = (existingTask, minTipPosition) => (dispatch) => {
  const minExistingTipPosition = dispatch(
    protocolManagementBusiness.getMinTipPositionByLabwareId(existingTask.labwareID)
  );
  const diffTipPosition = existingTask.tipPosition ? existingTask.tipPosition - minExistingTipPosition : 0;
  const diffMixingPosition = existingTask.mixingPositionAspirate ?
    existingTask.mixingPositionAspirate - minExistingTipPosition : 0;

  const newTipPosition = diffTipPosition < 1 && minTipPosition > 1 ?
    minTipPosition : parseFloat(parseFloat(minTipPosition + diffTipPosition).toFixed(2));
  const newMixingPosition = diffMixingPosition < 1 && minTipPosition > 1 ?
    minTipPosition : parseFloat(parseFloat(minTipPosition + diffMixingPosition).toFixed(2));

  return [newTipPosition, newMixingPosition];
};
*/

export function checkInvalidTask(
  cardId,
  checkOverAspirate = false,
  isCardSelected = true,
  // eslint-disable-next-line no-unused-vars
  saveProtocol = true, // FIXME: saveProtocol is no longer used. remove this argument in the future and adjust all consumers' call
  isReturnInvalidList = false,
  isHighVolume = false,
) {
  return (dispatch, getState) => {
    const {cardState, configState, availableLabwareState, protocolEditorState} = getState();
    const {tasks, cards} = cardState;

    const card = cards[cardId];
    const maxAspirateVolume = isHighVolume
      ? configState.maxAspirateHighVolume.value
      : configState.maxAspirateVolume.value;

    if (card.type !== CardType.SIMPLE_TRANSFER) {
      return false;
    }

    const isCardInProgress = Boolean(protocolEditorState.selectedTipChannel);
    const invalidTypeList = [];
    let volume = 0;
    let aspirateFlag = false;
    let anyAspirateFlag = false;
    let dispenseFlag = false;
    let ejectTipFlag = false;
    let pickTipFlag = false;
    let airgapFlag = false;
    let multipleAspirateFlag = false;
    let taskCount = 0;
    let tipCountMissMatch = false;
    let rowTipCount;
    let columnTipCount;

    if (card.tasks.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const id of card.tasks) {
        const task = tasks[id];
        const isCheckLabwareValidity = TASKS_NEED_VALID_LABWARE.includes(task.type);

        if (task.isIgnored) continue;

        taskCount += 1;

        if (
          task.type === TaskType.PICK_TIP ||
          task.type === TaskType.ASPIRATE ||
          task.type === TaskType.DISPENSE ||
          task.type === TaskType.MIXING ||
          task.type === TaskType.EJECT_TIP
        ) {
          if (!tipCountMissMatch && rowTipCount && columnTipCount) {
            tipCountMissMatch = rowTipCount !== task.rowTipCount || columnTipCount !== task.columnTipCount;
            if (tipCountMissMatch)
              invalidTypeList.push({
                error: CardInvalidType.REDEFINE,
                number: task.number,
              });
          } else if (!rowTipCount && !columnTipCount) {
            rowTipCount = task.rowTipCount;
            columnTipCount = task.columnTipCount;
          }
        }

        if (isCheckLabwareValidity) {
          // to get ID prefix, 2 characters after the labware ID need to be trimmed
          const taskLabwareId = parseInt(task?.labwareID?.slice(0, -2), 10);
          const isLabwareExists = availableLabwareState[taskLabwareId];

          if (!isLabwareExists)
            invalidTypeList.push({
              error: CardInvalidType.LABWARE_NOT_FOUND,
              number: task.number,
            });
        }

        switch (task.type) {
          case TaskType.ASPIRATE:
            volume = Math.round((parseFloat(volume) + parseFloat(task.volume)) * 10) / 10;

            if (task.volume === 0) {
              invalidTypeList.push({
                error: CardInvalidType.ZERO_ASPIRATE,
                number: task.number,
              });
            }

            if (aspirateFlag && volume > maxAspirateVolume) {
              invalidTypeList.push({
                error: CardInvalidType.ASPIRATE,
                number: task.number,
              });
            }

            if (ejectTipFlag) {
              invalidTypeList.push({
                error: CardInvalidType.EJECT_TIP,
                number: task.number,
              });
            }

            if (multipleAspirateFlag) {
              invalidTypeList.push({
                error: CardInvalidType.ASPIRATE,
                number: task.number,
              });
            }

            if (task.volume > maxAspirateVolume) {
              multipleAspirateFlag = true;
            }

            if (airgapFlag && volume > maxAspirateVolume) {
              invalidTypeList.push({
                error: CardInvalidType.AIR_GAP_VOLUME,
                number: task.number,
              });
            }

            aspirateFlag = true;
            anyAspirateFlag = true;

            break;
          case TaskType.DISPENSE:
            dispenseFlag = true;
            volume = Math.round((parseFloat(volume) - parseFloat(task.volume)) * 10) / 10;

            if (!aspirateFlag && volume < 0) {
              invalidTypeList.push({
                error: CardInvalidType.DISPENSE,
                number: task.number,
              });
            }

            if (volume < 0) {
              invalidTypeList.push({
                error: CardInvalidType.INSUFFICIENT_ASPIRATE_VOLUME,
                number: task.number,
              });
            }

            if (ejectTipFlag) {
              invalidTypeList.push({
                error: CardInvalidType.EJECT_TIP,
                number: task.number,
              });
            }

            if (volume === 0) {
              aspirateFlag = false;
              multipleAspirateFlag = false;
              airgapFlag = false;
            }

            break;
          case TaskType.PICK_TIP:
            if (task.number !== 1) {
              if (anyAspirateFlag || dispenseFlag || ejectTipFlag || pickTipFlag) {
                invalidTypeList.push({
                  error: CardInvalidType.PICK_TIP,
                  number: task.number,
                });
              }
            }

            if (ejectTipFlag) {
              invalidTypeList.push({
                error: CardInvalidType.EJECT_TIP,
                number: task.number,
              });
            }

            pickTipFlag = true;

            break;
          case TaskType.EJECT_TIP:
            ejectTipFlag = true;

            break;
          case TaskType.AIR_GAP:
            volume = Math.round((parseFloat(volume) + parseFloat(task.volume)) * 10) / 10;
            airgapFlag = true;

            if (task.number === 1 || ejectTipFlag) {
              invalidTypeList.push({
                error: CardInvalidType.AIR_GAP_ORDER,
                number: task.number,
              });
            }
            if (volume > maxAspirateVolume) {
              invalidTypeList.push({
                error: CardInvalidType.AIR_GAP_VOLUME,
                number: task.number,
              });
            }
            break;
          case TaskType.MIXING:
            if (ejectTipFlag || !pickTipFlag) {
              invalidTypeList.push({
                error: CardInvalidType.MIXING_WITHOUT_PICK_TIP,
                number: task.number,
              });
            }
            if (volume) {
              invalidTypeList.push({
                error: CardInvalidType.LIQUID_REMAINS_IN_TIP_FOR_MIXING,
                number: task.number,
              });
            }
            break;
          default:
            break;
        }
      }
    }

    if (!pickTipFlag && taskCount) {
      invalidTypeList.push({
        error: CardInvalidType.PICK_TIP_DOES_NOT_EXIST,
      });
    }

    if (checkOverAspirate && multipleAspirateFlag && volume > maxAspirateVolume) {
      invalidTypeList.push({
        error: CardInvalidType.OVER_ASPIRATE,
      });
    }

    if (!cards[cardId].isIgnored && !ejectTipFlag && (!isCardSelected || !isCardInProgress)) {
      invalidTypeList.push({
        error: CardInvalidType.EJECT_TIP_DOES_NOT_EXIST,
      });
    }

    if (isReturnInvalidList) {
      return invalidTypeList;
    }

    if (invalidTypeList.length === 0) {
      if (cards[cardId]?.invalidTypes?.length) {
        batch(() => {
          dispatch(cardStateAction.removeCardInvalidType(cardId));
        });
      }
    } else {
      batch(() => {
        dispatch(cardStateAction.addCardInvalidType(cardId, [...new Set(invalidTypeList)]));
      });
    }

    return invalidTypeList.length;
  };
}

export function refreshCardIgnoreStatus(cardId) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;
    const card = cards[cardId];

    const taskIds = [...card.tasks];

    if (!taskIds.length && card.type === CardType.SERIAL_DILUTION) {
      return;
    }

    const isActiveTaskExists = taskIds.find((id) => !tasks[id].isIgnored);
    const isAllTasksIgnored = isNil(isActiveTaskExists);

    batch(() => {
      dispatch(cardStateAction.setIgnoredCard(cardId, isAllTasksIgnored));
      if (isAllTasksIgnored) dispatch(cardStateAction.removeCardInvalidType(cardId));
    });
  };
}

export function setIgnoreAllCards(cardIds, isIgnore) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {cards, tasks} = cardState;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const newCards = jsonCopy(cards);
    const newTasks = jsonCopy(tasks);
    const dispenseLabwareIds = uniq(
      Object.values(tasks)
        .filter((task) => task.type === TaskType.DISPENSE)
        .map((task) => task.labwareID),
    );

    cardIds.forEach((cardId) => {
      const taskIds = newCards[cardId].tasks;

      taskIds.forEach((taskId) => {
        if (newTasks[taskId].type !== TaskType.INSERT_TASK) {
          newTasks[taskId].isIgnored = isIgnore;
        }
      });

      newCards[cardId].isIgnored = isIgnore;
    });

    batch(() => {
      dispatch(cardStateAction.restoreCardState(newCards));
      dispatch(cardStateAction.restoreCardTaskState(newTasks));
      dispatch(smarterDefaultDispenseTipPosition(dispenseLabwareIds));

      cardIds.forEach((cardId) => {
        if (newCards[cardId].type === CardType.SIMPLE_TRANSFER) {
          dispatch(
            checkInvalidTask(cardId, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY),
          );
        }
      });
    });
  };
}

export function setIgnoreAllTasks(cardId, isIgnore) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const taskIds = cardState.cards[cardId].tasks;
    batch(() => {
      for (let index = 0; index < taskIds.length; index += 1) {
        if (cardState.tasks[taskIds[index]].type !== TaskType.INSERT_TASK) {
          dispatch(cardStateAction.setIgnoredCardTask(taskIds[index], isIgnore));
        }
      }
      dispatch(cardStateAction.setIgnoredCard(cardId, isIgnore));
    });
  };
}

export function setIgnoreTask(taskId, isIgnore) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {tasks} = cardState;
    const task = tasks[taskId];

    batch(() => {
      dispatch(cardStateAction.setIgnoredCardTask(taskId, isIgnore));
      dispatch(smarterDefaultDispenseTipPosition([task.labwareID]));
    });
  };
}

export const getWellTarget = (labwareID, selectedTipChannel, row, column) => (dispatch, getState) => {
  let newRow = convertRowToAlphabet(row);
  let newColumn = column;

  const states = getState();
  const {labwareState} = states;

  const labware = find(labwareState, {LabwareID: labwareID});
  const {isIFC} = labware;
  let labwareWellCount = WellCount.WELL_384;
  if (labware) {
    const {colCount, rowCount} = labware;
    labwareWellCount = colCount * rowCount;
  }
  switch (selectedTipChannel) {
    case TipChannelType.TIP_CHANNEL_1: {
      break;
    }
    case TipChannelType.TIP_CHANNEL_8: {
      if ((newRow.charCodeAt(0) - 64) % 2 === 0 && (labwareWellCount === WellCount.WELL_384 || isIFC)) {
        newRow = 'B';
      } else {
        newRow = 'A';
      }
      break;
    }
    case TipChannelType.TIP_CHANNEL_12: {
      if (newColumn % 2 === 0 && (labwareWellCount === WellCount.WELL_384 || isIFC)) newColumn = 2;
      else newColumn = 1;
      break;
    }
    case TipChannelType.TIP_CHANNEL_96: {
      const rowIndxInNumber = newRow.charCodeAt(0) - 64;
      const isRowIndxEven = rowIndxInNumber % 2 === 0;
      const isColumnIndxEven = newColumn % 2 === 0;

      // quadrant 1
      newRow = 'A';
      newColumn = 1;
      if (labwareWellCount === WellCount.WELL_384 || isIFC) {
        if (!isRowIndxEven && isColumnIndxEven) {
          // quadrant 2
          newRow = 'A';
          newColumn = 2;
        } else if (isRowIndxEven && isColumnIndxEven) {
          // quadrant 3
          newRow = 'B';
          newColumn = 2;
        } else if (isRowIndxEven && !isColumnIndxEven) {
          // quadrant 4
          newRow = 'B';
          newColumn = 1;
        }
      }
      break;
    }
    default:
      break;
  }

  return [newRow, newColumn];
};

export function getTipChannelByTipCount(rowTipCount, columnTipCount) {
  const tipCount = rowTipCount * columnTipCount;

  if (
    !(rowTipCount === 1 && columnTipCount === 1) &&
    !(rowTipCount === 8 && columnTipCount === 1) &&
    !(rowTipCount === 1 && columnTipCount === 12) &&
    !(rowTipCount === 8 && columnTipCount === 12)
  ) {
    return null;
  }

  switch (tipCount) {
    case 1:
      return TipChannelType.TIP_CHANNEL_1;
    case 8:
      return TipChannelType.TIP_CHANNEL_8;
    case 12:
      return TipChannelType.TIP_CHANNEL_12;
    case 96:
      return TipChannelType.TIP_CHANNEL_96;
    default:
      return '';
  }
}

export const checkAndSetSelectedTipChannelByTask = (task) => (dispatch) => {
  if (isNil(task) || task?.type === TaskType.AIR_GAP) {
    dispatch(protocolEditorAction.setSelectedTipChannel(null));
    dispatch(protocolEditorAction.setCustomTipCount(null));

    return;
  }

  const {rowTipCount, columnTipCount} = task;

  const tipChannel = getTipChannelByTipCount(rowTipCount, columnTipCount);

  if (tipChannel) {
    dispatch(protocolEditorAction.setSelectedTipChannel(tipChannel));
    dispatch(protocolEditorAction.setCustomTipCount(null));

    return;
  }

  batch(() => {
    dispatch(protocolEditorAction.setSelectedTipChannel(TipChannelType.TIP_CHANNEL_CUSTOM));
    dispatch(protocolEditorAction.setCustomTipCount({row: rowTipCount, column: columnTipCount}));
  });
};

export const checkAndSetSelectedTipChannelByCardId = (cardId, isEdit, taskIdsToCheck) => (dispatch, getState) => {
  if (isNil(cardId)) {
    dispatch(protocolEditorAction.setSelectedTipChannel(null));
    dispatch(protocolEditorAction.setCustomTipCount(null));

    return;
  }

  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const card = cards[cardId];
  const taskIds = taskIdsToCheck || card.tasks;

  const cardTasks = filterToArray(tasks, {ID: taskIds});

  let pickTipTask;
  let ejectTipTask;

  let taskInsert;
  let transferTask;

  for (let i = 0; i < cardTasks.length; i += 1) {
    const task = cardTasks[i];

    if (task.type === TaskType.PICK_TIP) pickTipTask = task;
    if (task.type === TaskType.EJECT_TIP) ejectTipTask = task;
    if (task.type === TaskType.INSERT_TASK) taskInsert = task;

    if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
      transferTask = task;
    }

    if (ejectTipTask && pickTipTask && transferTask) break;
  }

  if ((pickTipTask || transferTask) && (!ejectTipTask || isEdit || taskInsert)) {
    // pick tip or eject tip is the priority to get the true tip channel number
    dispatch(checkAndSetSelectedTipChannelByTask(pickTipTask || transferTask));
  } else {
    dispatch(exitSimpleTransferCreationMode());
  }
};

export function setPrevCurrentTipVolume() {
  return (dispatch, getState) => {
    const {protocolEditorState} = getState();
    batch(() => {
      dispatch(protocolEditorAction.setCurrentTipVolumePrevState(protocolEditorState.currentTipVolume));
      dispatch(protocolEditorAction.setCurrentTipVolumeNextState(0));
    });
  };
}

export function setNextCurrentTipVolume() {
  return (dispatch, getState) => {
    const {protocolEditorState} = getState();
    batch(() => {
      dispatch(protocolEditorAction.setCurrentTipVolumePrevState(0));
      dispatch(protocolEditorAction.setCurrentTipVolumeNextState(protocolEditorState.currentTipVolume));
    });
  };
}

export function addSimpleTransferTask(
  type,
  selectedTipChannel,
  labwareID = 0,
  row = '',
  column = '',
  volume = 0,
  cardId = null,
  customTipCount = null,
  labwareIndex = 0,
) {
  return (dispatch, getState) => {
    const {cardState, configState, labwareState, protocolState} = getState();
    const {cards, tasks} = cardState;
    const {defaultTipPosition} = configState;
    const labware = find(labwareState, {LabwareID: labwareID});
    const deckNumber = Object.keys(labwareState).find((key) => labwareState[key]?.LabwareID === labware?.LabwareID);
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const taskType = type;

    let mixingTask;
    if (taskType === TaskType.MIXING) {
      mixingTask = find(tasks, {type: TaskType.MIXING, labwareID: undefined});
    }

    // eslint-disable-next-line prefer-const
    let [cardID, taskID, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.SIMPLE_TRANSFER,
      cardId,
      taskType,
      selectedTipChannel,
      customTipCount,
    );

    const cardName = cards[cardID]?.name || getNewCardName(cards, CardType.SIMPLE_TRANSFER);

    let rowTipCount;
    let columnTipCount;

    if (customTipCount) {
      rowTipCount = customTipCount.row;
      columnTipCount = customTipCount.column;
    } else {
      [rowTipCount, columnTipCount] = getTipCount(selectedTipChannel);
    }

    // eslint-disable-next-line no-shadow
    const insertTask = find(tasks, {
      ID: cards[cardID]?.tasks,
      type: TaskType.INSERT_TASK,
    });

    let startRow = row;
    let startColumn = column;

    if (customTipCount && labware) {
      let {rowCount: labwareRowCount, colCount: labwareColCount} = labware;
      const {isIFC} = labware;

      if (isIFC && labwareIndex) {
        const {labwareIndexes} = labware;
        labwareRowCount = labwareIndexes[labwareIndex].rowCount;
        labwareColCount = labwareIndexes[labwareIndex].colCount;
      }

      const labwareWellCount = labwareColCount * labwareRowCount;
      const is384 = labwareWellCount === WellCount.WELL_384;

      let skipWellRow = 0;
      let skipWellColumn = 0;

      if (is384 || isIFC) {
        skipWellRow = 1;
        skipWellColumn = 1;
      }

      const rowTipCountNeeded = skipWellRow === 0 ? rowTipCount : rowTipCount + (rowTipCount - 1) * skipWellRow;
      const columnTipCountNeeded =
        skipWellColumn === 0 ? columnTipCount : columnTipCount + (columnTipCount - 1) * skipWellColumn;

      if (startRow + rowTipCountNeeded > labwareRowCount) {
        startRow = labwareRowCount - rowTipCountNeeded + 1;
      }

      if (startColumn + columnTipCountNeeded > labwareColCount) {
        startColumn = labwareColCount - columnTipCountNeeded + 1;
      }

      if (is384 || isIFC) {
        if (rowTipCount === 8) {
          startRow = startRow >= 2 ? 2 : 1;
        } else if (columnTipCount === 12) {
          startColumn = startColumn >= 2 ? 2 : 1;
        }
      }

      // FIX ME hack for trough
      if (labwareColCount === 1) {
        startColumn = 1;
      }
      if (labwareRowCount === 1) {
        startRow = 1;
      }
    }

    const minTipPosition = dispatch(
      protocolManagementBusiness.getMinTipPositionByLabwareId(labwareID, false, labwareIndex),
    );

    if (insertTask && insertTask.ID) {
      taskID = insertTask.ID;
      number = insertTask.number;
    } else if (mixingTask && !isNil(mixingTask.ID)) {
      taskID = mixingTask.ID;
      number = mixingTask.number;
    }

    let task = {
      ID: taskID,
      number,
      type: taskType,
      labwareID,
      rowTipCount,
      columnTipCount,
      isIgnored: false,
    };

    if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE || task.type === TaskType.MIXING) {
      let actualRow;
      let actualColumn;

      if (customTipCount) {
        [actualRow, actualColumn] = dispatch(
          getWellTarget(task.labwareID, TipChannelType.TIP_CHANNEL_1, startRow, startColumn),
        );
      } else {
        [actualRow, actualColumn] = dispatch(getWellTarget(task.labwareID, selectedTipChannel, startRow, startColumn));
      }

      let taskTipTouchOffProperties = {
        tipTouchOff: false,
        tipTouchOffDelay: 0,
        tipTouchOffHeight: labware?.wellDepth,
      };

      const maxAspirateVolume =
        protocolTrayType === TrayType.HIGH_VOLUME_CADDY
          ? configState.maxAspirateHighVolume.value
          : configState.maxAspirateVolume.value;
      let mixingVolume = volume > maxAspirateVolume ? maxAspirateVolume : volume;

      if (task.type === TaskType.MIXING) {
        taskTipTouchOffProperties = {};
        const {validatedVolume: validatedMixingVolume} = dispatch(validateMinVolumeAllowed(mixingVolume));
        mixingVolume = validatedMixingVolume;
      }

      const rangeSpeed = dispatch(getRangeSpeedPipettingParameter(task));
      const rangeMixingSpeed = dispatch(getRangeMixingSpeedPipettingParameter(task));
      const {validatedVolume} = dispatch(validateMinVolumeAllowed(volume));

      task = {
        ...task,
        ...taskTipTouchOffProperties,
        volume: validatedVolume,
        enableMixing: task.type === TaskType.MIXING,
        mixingCycle: 3,
        mixingVolume,
        tipPosition: minTipPosition,
        mixingPositionAspirate: minTipPosition,
        mixingPositionDispense: minTipPosition,
        speed: rangeSpeed.default,
        retractSpeed: 20,
        mixingSpeed: rangeMixingSpeed.default,
        row: actualRow,
        column: actualColumn,
      };

      if (labwareIndex && labware.isIFC) {
        task = {
          ...task,
          labwareIndex,
        };
      }
      task = setDefaultTipTouchOffDirectionTasks(labware, task);
    }

    if (task.type === TaskType.DISPENSE) {
      task = {...task, ...DefaultPipettingParameterValue[TaskType.DISPENSE]};

      if (task.tipPosition === defaultTipPosition) {
        task = {
          ...task,
          tipPosition: dispatch(getDefaultTipPositionByVolume(task.volume)),
        };
      }
    }

    if (task.type === TaskType.EJECT_TIP) {
      task = {
        ...task,
        changeTip: false,
      };
    }

    const groupID = cards[cardID]?.groupID ?? null;

    batch(() => {
      if (task.type === TaskType.PICK_TIP) {
        Object.keys(cardState).forEach((ID) => {
          if (cardState[ID].type === CardType.SIMPLE_TRANSFER) {
            const hasEjectTipTask = !!find(tasks, {
              ID: cardState[ID]?.tasks,
              type: TaskType.EJECT_TIP,
            });

            if (!hasEjectTipTask) {
              dispatch(
                checkInvalidTask(ID, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY),
              );
            }
          }
        });
      }

      if (groupID !== null && groupID !== undefined) {
        dispatch(cardStateAction.setGroupIsExpanded(groupID, true));
      }

      if (insertTask) {
        dispatch(cardStateAction.setIsInsertCard(cardID, false));
      }

      if (type === TaskType.MIXING && mixingTask) {
        dispatch(protocolEditorAction.setSimpleTransferEditedType(null));
      }

      dispatch(cardStateAction.setCardIsSelected(cardID));
      dispatch(cardStateAction.addCardTask(cardID, cardName, CardType.SIMPLE_TRANSFER, task.ID, task));
      dispatch(checkInvalidTask(cardID, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      dispatch(setPrevCurrentTipVolume());
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));
      if (task.type === TaskType.DISPENSE) {
        dispatch(smarterDefaultDispenseTipPosition([labwareID]));
      }

      if (protocolTrayType === TrayType.HIGH_VOLUME_CADDY && Number(deckNumber) === 5 && minTipPosition !== 1) {
        dispatch(
          appStateAction.showSnackbar(
            Feedback.WARNING,
            <FormattedHTMLMessage
              id="protocolEditor.taskList.pipettingProfile.setToDefaultDeck5"
              values={{tipPosition: minTipPosition}}
            />,
          ),
        );
      }
    });
  };
}

export function editSimpleTransferWellTargetTask(
  taskID,
  labwareID,
  selectedTipChannel,
  row,
  column,
  cardID,
  customTipCount,
  labwareIndex = 0,
) {
  return (dispatch, getState) => {
    const {cardState, labwareState, protocolState} = getState();
    const {tasks, cards} = cardState;

    const task = tasks[taskID];
    const labware = find(labwareState, {LabwareID: labwareID});
    const prevLabwareID = task.labwareID;
    const prevLabwareIndex = task.labwareIndex;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const deckNumber = Object.keys(labwareState).find((key) => labwareState[key]?.LabwareID === labware?.LabwareID);

    // find the next task for air gap checking
    const card = cards[cardID];
    const taskIndex = card.tasks.findIndex((e) => e === taskID);
    const nextTask = tasks[card.tasks[taskIndex + 1]];

    let validRow;
    let validColumn;
    let rowTipCount;
    let columnTipCount;

    if (customTipCount) {
      [validRow, validColumn] = dispatch(getWellTarget(labwareID, TipChannelType.TIP_CHANNEL_1, row, column));
      rowTipCount = customTipCount.row;
      columnTipCount = customTipCount.column;
    } else {
      [validRow, validColumn] = dispatch(getWellTarget(labwareID, selectedTipChannel, row, column));
      [rowTipCount, columnTipCount] = getTipCount(selectedTipChannel);
    }

    const prevMinTipPosition = dispatch(
      protocolManagementBusiness.getMinTipPositionByLabwareId(prevLabwareID, false, prevLabwareIndex),
    );
    const minTipPosition = dispatch(
      protocolManagementBusiness.getMinTipPositionByLabwareId(labwareID, false, labwareIndex),
    );
    const maxTipPosisiton = parseFloat((Math.floor((labware?.wellDepth ?? 0) * 100) / 100).toFixed(2));

    let showMessage = false;

    return batch(() => {
      dispatch(cardStateAction.setTaskRow(taskID, validRow));
      dispatch(cardStateAction.setTaskColumn(taskID, validColumn));
      dispatch(cardStateAction.setTaskLabware(taskID, labwareID));
      dispatch(cardStateAction.setTaskLabwareIndex(taskID, labwareIndex));
      dispatch(cardStateAction.setTaskRowTipCount(taskID, rowTipCount));
      dispatch(cardStateAction.setTaskColumnTipCount(taskID, columnTipCount));

      if (nextTask && nextTask.type === TaskType.AIR_GAP) {
        dispatch(cardStateAction.setTaskLabware(nextTask.ID, labwareID));
      }

      if (rowTipCount > labware.rowCount || columnTipCount > labware.colCount) {
        dispatch(cardStateAction.setTaskTipTouchOff(taskID, false));
        dispatch(cardStateAction.setTaskDispenseAtWellSide(taskID, false));
      }

      if (prevMinTipPosition !== minTipPosition) {
        if (task.tipPosition < minTipPosition || task.tipPosition === prevMinTipPosition) {
          dispatch(cardStateAction.setTaskTipPosition(taskID, minTipPosition));
          showMessage = true;
        }

        if (task.mixingPositionAspirate < minTipPosition || task.mixingPositionAspirate === prevMinTipPosition) {
          dispatch(cardStateAction.setTaskMixingPositionAspirate(taskID, minTipPosition));
          showMessage = true;
        }

        if (task.mixingPositionDispense < minTipPosition || task.mixingPositionDispense === prevMinTipPosition) {
          dispatch(cardStateAction.setTaskMixingPositionDispense(taskID, minTipPosition));
          showMessage = true;
        }

        if (task.tipPosition > maxTipPosisiton) {
          dispatch(cardStateAction.setTaskTipPosition(taskID, maxTipPosisiton));
          showMessage = true;
        }

        if (task.mixingPositionAspirate > maxTipPosisiton) {
          dispatch(cardStateAction.setTaskMixingPositionAspirate(taskID, maxTipPosisiton));
          showMessage = true;
        }

        if (task.mixingPositionDispense > maxTipPosisiton) {
          dispatch(cardStateAction.setTaskMixingPositionDispense(taskID, maxTipPosisiton));
          showMessage = true;
        }

        if (showMessage && protocolTrayType === TrayType.HIGH_VOLUME_CADDY && Number(deckNumber) === 5) {
          dispatch(
            appStateAction.showSnackbar(
              Feedback.WARNING,
              <FormattedHTMLMessage
                id="protocolEditor.taskList.pipettingProfile.setToDefaultDeck5"
                values={{tipPosition: minTipPosition}}
              />,
            ),
          );
        } else if (showMessage) {
          dispatch(
            appStateAction.showSnackbar(
              Feedback.WARNING,
              intl.formatMessage({
                id: 'protocolEditor.taskList.pipettingProfile.setToDefault',
              }),
            ),
          );
        }
      }

      if (task.type === TaskType.DISPENSE) {
        if (prevLabwareID === labwareID) {
          dispatch(smarterDefaultDispenseTipPosition([labwareID]));
        } else {
          dispatch(smarterDefaultDispenseTipPosition([prevLabwareID, labwareID]));
        }
      }

      if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
        const tipTouchOffDirection = setDefaultTipTouchOffDirectionTasks(labware, task);
        dispatch(cardStateAction.setTaskTipTouchOffHeight(task.ID, labware?.wellDepth));
        dispatch(cardStateAction.setTaskTipTouchOffDirectionX(task.ID, tipTouchOffDirection.tipTouchOffDirectionX));
        dispatch(cardStateAction.setTaskTipTouchOffDirectionY(task.ID, tipTouchOffDirection.tipTouchOffDirectionY));
      }

      dispatch(checkInvalidTask(cardID, false, true, true, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));

      const newCardState = getState().cardState;
      return newCardState.tasks[taskID];
    });
  };
}

export function editSimpleTransferVolumeTask(cardID, taskID, volume, taskType, isSkipEjectTipChecking = true) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {tasks} = cardState;
    const {labwareID} = tasks[taskID];
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const {validatedVolume} = dispatch(validateMinVolumeAllowed(volume));

    batch(() => {
      if (taskType === TaskType.MIXING) {
        dispatch(cardStateAction.setTaskMixingVolume(taskID, validatedVolume));
      }

      dispatch(cardStateAction.setTaskVolume(taskID, validatedVolume, taskType));
      dispatch(
        checkInvalidTask(
          cardID,
          false,
          isSkipEjectTipChecking,
          false,
          false,
          protocolTrayType === TrayType.HIGH_VOLUME_CADDY,
        ),
      );
      dispatch(smarterDefaultDispenseTipPosition([labwareID]));
    });
  };
}

export function editSimpleTransferTipChannel(cardID, selectedTipChannel, customTipCount) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = cards[cardID];

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    if (selectedCard.type !== CardType.SIMPLE_TRANSFER) {
      return;
    }

    const pickTipTask = Object.values(tasks).find(
      (task) => selectedCard.tasks.includes(task.ID) && task.type === TaskType.PICK_TIP,
    );
    const ejectTipTask = Object.values(tasks).find(
      (task) => selectedCard.tasks.includes(task.ID) && task.type === TaskType.EJECT_TIP,
    );

    let rowTipCount;
    let columnTipCount;

    if (selectedTipChannel) {
      [rowTipCount, columnTipCount] = getTipCount(selectedTipChannel);
    }

    if (customTipCount) {
      rowTipCount = customTipCount.row;
      columnTipCount = customTipCount.column;
    }

    pickTipTask.rowTipCount = rowTipCount;
    pickTipTask.columnTipCount = columnTipCount;

    if (ejectTipTask) {
      ejectTipTask.rowTipCount = rowTipCount;
      ejectTipTask.columnTipCount = columnTipCount;
    }

    batch(() => {
      dispatch(cardStateAction.restoreCardTaskStateById(pickTipTask));
      if (ejectTipTask) dispatch(cardStateAction.restoreCardTaskStateById(ejectTipTask));
      dispatch(checkInvalidTask(cardID, false, true, true, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));
    });
  };
}

export function duplicateSimpleTransferCard(card, taskList, rowShift = 0, colShift = 0, taskToShiftIDs = []) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {cards, tasks} = cardState;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const [cardID, taskID, number] = generateCardIDAndTaskIDAndNumber(cards, tasks, card.type);
    const cardName = cards[cardID]?.name || getNewCardName(cards, card.type);
    batch(() => {
      let i = 0;
      taskList.forEach((task) => {
        const newTask = {
          ...task,
          ID: taskID + i,
          number: number + i,
          column: !taskToShiftIDs.includes(task.ID) ? task.column : task.column * 2 - 1 + colShift,
          row: !taskToShiftIDs.includes(task.ID)
            ? task.row
            : convertRowToAlphabet(convertRowToNumeric(task.row) * 2 - 1 + rowShift),
        };

        dispatch(cardStateAction.addCardTask(cardID, cardName, card.type, newTask.ID, newTask));
        i += 1;
      });

      dispatch(checkInvalidTask(cardID, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      dispatch(cardStateAction.setAllTaskIsSelected(false));
      dispatch(cardStateAction.setCardIsSelected(cardID));
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));
    });
  };
}

export function handlePlateSwapDuplicateInSimpleTransfer(labwareID, choosenQuadrant, quadrantShifter) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const simpleTransferCards = filterToArray(cards, {type: CardType.SIMPLE_TRANSFER});
    simpleTransferCards.forEach((card) => {
      const existSimpleTransferTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      if (existSimpleTransferTasks.length) {
        let currentTasks = filterToArray(tasks, {ID: card.tasks});
        // sort by number
        /* eslint-disable-next-line no-nested-ternary */
        currentTasks = currentTasks.sort((a, b) => (a.number > b.number ? 1 : b.number > a.number ? -1 : 0));
        choosenQuadrant.forEach((q) => {
          dispatch(
            duplicateSimpleTransferCard(
              card,
              currentTasks,
              quadrantShifter[q].row,
              quadrantShifter[q].column,
              existSimpleTransferTasks.map((task) => task.ID),
            ),
          );
        });
      }
    });
  };
}

export function editGeneralTask(cardID, task) {
  return (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const cardIgnored = cards[cardID].isIgnored;

    batch(() => {
      if (cards[cardID].isInsertTask) {
        dispatch(cardStateAction.setIsInsertCard(cardID, false));
      }
      dispatch(cardStateAction.setCardIsSelected(cardID));
      dispatch(cardStateAction.restoreCardTaskStateById(task));
      if (cardIgnored) {
        dispatch(cardStateAction.setIgnoredCard(cardID, true));
      }
      dispatch(globalAddUndoItem());
    });
  };
}

export function addGeneralTask(cardId, _cardType, taskData, callback) {
  return (dispatch, getState) => {
    const {cardState, configState, protocolState} = getState();
    const {cards, tasks, groups} = cardState;
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const maxAspirateVolume =
      protocolTrayType === TrayType.HIGH_VOLUME_CADDY
        ? configState.maxAspirateHighVolume.value
        : configState.maxAspirateVolume.value;

    let cardType = _cardType;

    const card = cards[cardId];

    let cardName = getNewCardName(cards, cardType);

    if (card) {
      if (card.type === CardType.SIMPLE_TRANSFER) {
        cardType = card.type;
        cardName = card.name;
      }
    }

    const [cardID, taskID, number, groupId] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      cardType,
      cardId,
      null,
      null,
      null,
      groups,
    );

    // eslint-disable-next-line no-shadow
    const insertTask = find(tasks, {
      ID: card?.tasks,
      type: TaskType.INSERT_TASK,
    });

    if (taskData.volume !== undefined) {
      taskData.maxVolume = maxAspirateVolume;
    }

    const task = {
      ID: (insertTask && insertTask.ID) || taskID,
      number: (insertTask && insertTask.number) || number,
      ...taskData,
    };

    batch(() => {
      dispatch(cardStateAction.setCardIsSelected(cardID));
      dispatch(cardStateAction.setCardIsExpanded());
      dispatch(cardStateAction.addCardTask(cardID, cardName, cardType, task.ID, task, groupId));
      if (taskData.volume) {
        dispatch(checkInvalidTask(cardId, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      }
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));
      dispatch(globalAddUndoItem());
      if (callback) callback(task);
    });
  };
}

export function insertTask(cardId, taskNumber, undoRedoTask = null) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {cards, tasks} = cardState;

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const oldCardState = jsonCopy(cardState);

    const card = cards[cardId];
    const cardName = card.name;
    const cardType = card.type;

    const [, taskID] = generateCardIDAndTaskIDAndNumber(cards, tasks, cardType, cardId);

    if (undoRedoTask) {
      undoRedoTask.ID = taskID;
    }

    const task = undoRedoTask || {
      ID: taskID,
      number: taskNumber + 1,
      type: TaskType.INSERT_TASK,
      isIgnored: true,
      volume: 0,
    };

    batch(() => {
      if (!undoRedoTask) {
        dispatch(cardStateAction.setIsInsertCard(cardId, true));
      } else if (!undoRedoTask.isIgnored) {
        dispatch(cardStateAction.setIgnoredCard(cardId, false));
      }
      dispatch(cardStateAction.setCardIsSelected(cardId));
      dispatch(cardStateAction.insertCardTask(cardId, cardName, cardType, task.ID, task, taskNumber - 1));
      for (let i = taskNumber; i < card.tasks.length; i++) {
        dispatch(cardStateAction.setTaskNumber(card.tasks[i], i + 1));
      }
      dispatch(checkInvalidTask(cardId, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      dispatch(globalAddUndoItem(oldCardState));
    });
  };
}

export function selectCard(cardId, unselectOther = true, selectStatus = true) {
  return (dispatch, getState) => {
    const {appState, runtimeState} = getState();
    const {viewMode} = appState;
    const {status} = runtimeState;

    if (
      viewMode === ViewMode.RUNTIME &&
      (status === RuntimeMode.RUNNING ||
        status === RuntimeMode.PAUSING ||
        status === RuntimeMode.RUNTIME_PREPARATION ||
        status === RuntimeMode.ABORTING)
    ) {
      return;
    }

    dispatch(cardStateAction.setCardIsSelected(cardId, unselectOther, selectStatus));
  };
}

export const unselectAllTaskInCard = (card) => (dispatch, getState) => {
  const {cardState} = getState();
  const {tasks} = cardState;

  const updatedTasks = [];
  const {tasks: taskIds} = card || {};

  if (!card || !taskIds) return;

  taskIds.forEach((id) => {
    updatedTasks.push({...tasks[id], isSelected: false});
  });

  dispatch(cardStateAction.batchRestoreCardTaskStateById(updatedTasks));
};

export function rearrangeTaskItemList(taskId, swapTaskId, cardId) {
  return (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {tasks} = cardState;
    const task = tasks[taskId];
    const swapTask = tasks[swapTaskId];

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const labwareIDs = task.labwareID === swapTask.labwareID ? [task.labwareID] : [task.labwareID, swapTask.labwareID];

    batch(() => {
      dispatch(cardStateAction.swabTaskItemPosition(cardId, taskId, swapTaskId));
      dispatch(checkInvalidTask(cardId, false, true, false, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
      dispatch(smarterDefaultDispenseTipPosition(labwareIDs));
      dispatch(protocolManagementBusiness.restoreTipCountForCards([cardId]));
    });
  };
}

export function removeCardTask(cardId, taskId, skipAddUndoItem = false) {
  return (dispatch, getState) => {
    const {cardState: cardStateBefore, protocolState} = getState();
    const {tasks} = cardStateBefore;
    const task = tasks[taskId];

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    batch(() => {
      dispatch(setPrevCurrentTipVolume());
      if (task.type === TaskType.INSERT_TASK || cardStateBefore.cards[cardId]?.isInsertTask) {
        dispatch(cardStateAction.setIsInsertCard(cardId, false));
      }
      dispatch(cardStateAction.removeCardTask(cardId, taskId));
    });
    const {cardState: cardStateAfter} = getState();
    const {cards} = cardStateAfter;
    let count = 1;
    // eslint-disable-next-line no-restricted-syntax
    for (const id of cards[cardId].tasks) {
      if (id !== taskId) {
        dispatch(cardStateAction.setTaskNumber(id, count));
        count++;
      }
    }
    dispatch(checkInvalidTask(cardId, false, true, true, false, protocolTrayType === TrayType.HIGH_VOLUME_CADDY));
    if (task.type === TaskType.DISPENSE) {
      dispatch(smarterDefaultDispenseTipPosition([task.labwareID]));
    }
    if (!skipAddUndoItem) {
      dispatch(globalAddUndoItem());
    }
  };
}

export function deleteCard(cardId, skipAddUndoItem = false) {
  return async (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const card = cards[cardId];

    if (!card) {
      return;
    }

    const groupID = cardState.cards[cardId]?.groupID;
    batch(() => {
      dispatch(cardStateAction.removeCard(cardId));
      if (groupID !== undefined && groupID !== null) {
        if (cardState.groups[groupID].cards.length > 1) {
          dispatch(cardStateAction.removeCardGroupCards(groupID, [cardId]));
        } else {
          dispatch(cardStateAction.removeCardGroups([groupID]));
        }
      }
      dispatch(smarterDefaultDispenseTipPosition());
      if (!skipAddUndoItem) {
        dispatch(globalAddUndoItem());
      }
    });
  };
}

/**
 * Function that rearranges card & cardObject and storing the rearranged card to redux state.
 * @param {Number} start the cardId starting point to rearrange.
 * @param {Array} cardIDs ids of card(s) that wants to be rearranged.
 * @param {Object} opt optional properties:
 * isSelectCard: is the specified cardIds should be selected after rearrangement?
 * skipAddUndoItem: should a global undo item be added after process?
 * cardObject: a custom cardObject that is not taken from the lastOpenedProtocol's properties.
 * @returns void.
 */
export const rearrangeAndApplyCards =
  (start, cardIDs, opt = {isSelectCard: false, skipAddUndoItem: false, cardObject: {}}) =>
  (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {lastOpenedProtocol} = protocolState || {};

    const {isSelectCard, skipAddUndoItem, cardObject} = opt || {};

    const [newCards, newGroups, newAndOldIds] = rearrangeCards(start, cardIDs, cardState, {
      isSelectCard,
    });

    const newCardObject = rearrangeCardObject(newAndOldIds, cardObject || lastOpenedProtocol?.cardObject);
    const updatedProtocol = {...lastOpenedProtocol, cardObject: newCardObject};

    batch(() => {
      dispatch(cardStateAction.restoreCardState(newCards));
      dispatch(cardStateAction.restoreGroupState(newGroups));
      dispatch(setLastOpenedProtocol(updatedProtocol));
      if (!skipAddUndoItem) {
        dispatch(smarterDefaultDispenseTipPosition());
        dispatch(globalAddUndoItem());
      }
    });
  };

export function deleteMultipleCardsAndGroups(cardIds = [], skipAddUndoItem = false) {
  return (dispatch, getState) => {
    const {cardState} = getState();

    if (cardIds.length === 0) return;

    const taskIds = [];
    const groupIDs = {};

    cardIds.forEach((cardId) => {
      const tasks = cardState.cards[cardId]?.tasks;
      const groupID = cardState.cards[cardId]?.groupID;
      const prevCardList = groupIDs[groupID] || [];

      if (tasks?.length) taskIds.push(...tasks);
      if (groupID !== undefined && groupID !== null) {
        groupIDs[groupID] = [...prevCardList, cardId];
      }
    });

    batch(() => {
      dispatch(cardStateAction.removeCards(cardIds));
      Object.keys(groupIDs).forEach((groupID) => {
        const cardList = groupIDs[groupID];
        if (cardState.groups[groupID].cards.length === cardList.length) {
          dispatch(cardStateAction.removeCardGroups([groupID]));
        } else {
          dispatch(cardStateAction.removeCardGroupCards(groupID, cardList));
        }
      });
      dispatch(smarterDefaultDispenseTipPosition());
      if (!skipAddUndoItem) {
        dispatch(globalAddUndoItem());
      }
    });
  };
}

const copyTask =
  (taskId, runtimeMode = false, allCards = false) =>
  async (dispatch, getState) => {
    const {cardState, labwareState, runtimeState} = getState();
    const {cards, tasks} = cardState;
    const {csvRecordID, doneRecordCSVIDs} = runtimeState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return false;
    }

    const selectedTask = find(tasks, {ID: taskId});

    if (!selectedTask) {
      return false;
    }

    const selectedTaskLabware = find(labwareState, {LabwareID: selectedTask.labwareID});
    const selectedTaskPipettingParam = extractPipettingParameterFromTask(selectedTask);
    const taskSource = {pipettingParameters: selectedTaskPipettingParam, labware: selectedTaskLabware};

    let simpleTransferCardsTasks = [];

    filterToArray(cards, {type: CardType.SIMPLE_TRANSFER}).forEach((e) => {
      simpleTransferCardsTasks = simpleTransferCardsTasks.concat(e.tasks);
    });

    const filter = allCards
      ? {ID: simpleTransferCardsTasks, type: selectedTask.type}
      : {ID: selectedCard.tasks, type: selectedTask.type};

    const excludedTaskIds = runtimeMode ? [taskId, csvRecordID, ...doneRecordCSVIDs] : [];

    const toUpdateTasks = filterToArray(tasks, filter)
      .filter((e) => !excludedTaskIds.includes(e.ID))
      .map((e) => {
        const labware = find(labwareState, {LabwareID: e.labwareID});
        const taskDestination = {pipettingParameters: e, labware};

        const appliedPipettingParameters = dispatch(getAppliedPipettingParameters(taskSource, taskDestination));
        return appliedPipettingParameters;
      });

    if (!toUpdateTasks.length) {
      return false;
    }

    dispatch(cardStateAction.batchRestoreCardTaskStateById(toUpdateTasks));

    if (runtimeMode) {
      await dispatch(protocolManagementBusiness.updateRunningProtocol());
    }

    return true;
  };

export const copyTaskSameCard = (taskId, runtimeMode) => (dispatch) => dispatch(copyTask(taskId, runtimeMode));

export const copyTaskAllCards = (taskId, runtimeMode) => (dispatch) => dispatch(copyTask(taskId, runtimeMode, true));

export const onSimpleTransferReuseTipsChange = (isReuseTips) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  const ejectTipTask = find(tasks, {
    ID: selectedCard.tasks,
    type: TaskType.EJECT_TIP,
  });

  if (!ejectTipTask) {
    return;
  }

  dispatch(
    cardStateAction.restoreCardTaskStateById({
      ...ejectTipTask,
      changeTip: isReuseTips,
    }),
  );
  dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(selectedCard.ID));
};

// #region card grouping
export function getNewGroupName(groups, initialName) {
  const prefix = initialName ?? 'Group';
  // eslint-disable-next-line arrow-body-style
  const associatedGroups = filterToArray(groups, ({name}) => {
    return String(name).includes(prefix);
  });

  if (!associatedGroups.length) {
    return `${prefix} 1`;
  }

  const splittedPrefix = prefix.split(' ');

  const maxCardIndex = associatedGroups.reduce((max, card) => {
    const splittedNames = card.name.split(' ');
    let index = parseInt(splittedNames[splittedPrefix.length], 10);
    if (Number.isNaN(index)) {
      index = 0;
    }
    return Math.max(max, index);
  }, 1);

  return `${prefix} ${maxCardIndex + 1}`;
}

export const addCardGroup =
  (
    // eslint-disable-next-line default-param-last
    cardIDs = [],
    // eslint-disable-next-line default-param-last
    startID = null,
    // eslint-disable-next-line default-param-last
    groupID = null,
    // eslint-disable-next-line default-param-last
    isSelectCard = true,
    // eslint-disable-next-line default-param-last
    skipAddUndoItem = false,
    initialName,
    isRearrangeAfter = true,
  ) =>
  (dispatch, getState) => {
    const {cardState, protocolState} = getState();
    const {groups, cards} = cardState;

    let currentGroupId = groupID || 0;
    const currentGroup = groups[groupID] || {};

    if (groupID === null || groupID === undefined) {
      if (Object.keys(groups).length) currentGroupId = Math.max(...Object.keys(groups)) + 1;
    }

    const groupName = currentGroup?.name || getNewGroupName(groups, initialName);
    const currentCardList = currentGroup?.cards || [];
    const cardList = [...currentCardList, ...cardIDs];

    const group = {
      ID: currentGroupId,
      name: groupName,
      cards: uniq(cardList).sort((a, b) => a - b),
      isSelected: currentGroup?.isSelected ?? true,
      isExpanded: currentGroup?.isExpanded ?? false,
    };

    batch(() => {
      // remove cardID from previous group
      cardIDs.forEach((cardID) => {
        const prevGroupID = cards[cardID].groupID;
        if (prevGroupID !== null && prevGroupID !== undefined) {
          dispatch(cardStateAction.removeCardGroupCards(prevGroupID, cardList));
        }
      });
      dispatch(cardStateAction.addCardGroup(currentGroupId, group, cardIDs));
      if ((groupID !== null && groupID !== undefined) || cardIDs.length > 1) {
        const start = startID ?? cardIDs[0];
        const newItemIDs = groupID === null || groupID === undefined ? cardIDs.slice(1) : cardIDs;

        if (isRearrangeAfter) {
          const newCardObject = generateCardObject(cardState, protocolState?.lastOpenedProtocol?.cardObject);
          const rearrangeOptions = {
            isSelectCard,
            skipAddUndoItem: true,
            cardObject: lowercaseEveryKeysFirstWord(newCardObject),
          };

          // FIXME: This code is SIDE-EFFECT! Rearrange can cause the card id changed, need to update the return value of group
          dispatch(rearrangeAndApplyCards(start, newItemIDs, rearrangeOptions));
        }
      }
      if (!skipAddUndoItem) {
        dispatch(smarterDefaultDispenseTipPosition());
        dispatch(globalAddUndoItem());
      }
    });

    return group;
  };

export const ungroupCardGroup =
  (groupIDs = [], skipAddUndoItem = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {groups, cards} = cardState;

    if (!groupIDs.length) return;

    const cardList = [];

    groupIDs.forEach((groupID) => {
      const selectedGroup = groups[groupID];
      const {cards: cardIds} = selectedGroup;

      cardIds.forEach((ID) => {
        const card = cards[ID];
        if (card) {
          const cardCopy = jsonCopy(card);
          cardCopy.groupID = null;
          cardList.push(cardCopy);
        }
      });
    });

    batch(() => {
      dispatch(cardStateAction.batchRestoreCardStateById(cardList));
      dispatch(cardStateAction.removeCardGroups(groupIDs));
      if (!skipAddUndoItem) {
        dispatch(globalAddUndoItem());
      }
    });
  };

export const rearrangeCardFromGroupCard =
  (cardIDs, start, newGroupID = null, isAddGroup = false, skipAddUndoItem = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, groups} = cardState;

    if (!cardIDs.length) return;

    const selectedGroups = Object.values(groups).filter((g) => g.isSelected);

    batch(() => {
      cardIDs.forEach((cardID) => {
        const selectedCard = cards[cardID];
        const previousGroupID = selectedCard?.groupID;
        const selectedGroup = groups[previousGroupID];
        const isPreviousGroupSelected = selectedGroups.find((g) => g.ID === previousGroupID);
        if (selectedGroup && !isPreviousGroupSelected) {
          selectedCard.groupID = null;
          dispatch(cardStateAction.restoreCardStateById(selectedCard));
          if (selectedGroup?.cards?.length > 1) {
            dispatch(cardStateAction.removeCardGroupCards(previousGroupID, [cardID]));
          } else {
            dispatch(cardStateAction.removeCardGroups([previousGroupID]));
          }
        }
      });
      if (isAddGroup) {
        dispatch(addCardGroup(cardIDs, start, newGroupID, false, true));
      } else {
        dispatch(rearrangeAndApplyCards(start, cardIDs, {skipAddUndoItem: true}));
      }
      if (!skipAddUndoItem) {
        dispatch(smarterDefaultDispenseTipPosition());
        dispatch(globalAddUndoItem());
      }
    });
  };

// #endregion card grouping

// #region Normalization Old UI

export function openNormalizationEditor(labware, cardID = undefined) {
  return (dispatch, getState) => {
    batch(() => {
      dispatch(protocolEditorAction.setSelectedLabwareID(labware.LabwareID));
      dispatch(normalizationStateAction.setNormalizationSelectedLabware(labware));
      dispatch(appStateAction.changeView(ViewMode.NORMALIZATION_OLD));
      if (cardID !== undefined) {
        const {cardState} = getState();
        const {cards, tasks} = cardState;
        dispatch(temporaryStateAction.addTemporaryState(TemporaryStateType.CARDS, cards[cardID]));
        dispatch(temporaryStateAction.addTemporaryState(TemporaryStateType.TASKS, tasks));
        dispatch(
          normalizationStateAction.restoreNormalizationState({
            ...cards[cardID].normalizationState,
            currentWizardView: 2,
          }),
        );
        dispatch(cardStateAction.setCardIsSelected(cardID));
        dispatch(protocolEditorAction.setSelectedLabwareID(tasks[cards[cardID].tasks[2]].labwareID));
        for (let index = 0; index < cards[cardID].tasks.length; index++) {
          const taskId = cards[cardID].tasks[index];
          dispatch(cardStateAction.removeCardTask(cardID, taskId));
        }
      }
    });
  };
}

// OBSOLETE FUNCTION
export function finishNormalization() {
  return (dispatch, getState) => {
    const {normalizationState, cardState, temporaryState, configState, protocolEditorState} = getState();
    const {previewData, selectedDiluentLabwareID} = normalizationState;

    batch(() => {
      const {cards, tasks} = cardState;
      const isEditing = temporaryState?.CARDS?.ID !== undefined;

      let cardID;
      let cardName;

      if (isEditing) {
        cardID = temporaryState.CARDS.ID;
        cardName = temporaryState.CARDS.name;
      } else {
        cardID = Object.keys(cards).length === 0 ? 0 : Math.max(...Object.keys(cards)) + 1;
        cardName = getNewCardName(cards, CardType.NORMALIZATION);
      }

      batch(() => {
        let newTaskID = Object.keys(tasks).length === 0 ? 0 : Math.max(...Object.keys(tasks)) + 1;
        let number = 0;

        const minTipPositionDiluentLabware = dispatch(protocolManagementBusiness.getMinTipPositionByLabwareId(0));
        const minTipPositionTargetLabware = dispatch(protocolManagementBusiness.getMinTipPositionByLabwareId(0));

        for (let iPrev = 0; iPrev < previewData.length; iPrev += 1) {
          const {status, row, col, volume: diluentVol} = previewData[iPrev];

          if (status === NormalizationPreviewWellStatus.NORMAL) {
            const {maxAspirateVolume} = configState;
            const cycle = diluentVol < maxAspirateVolume.value ? 1 : Math.floor(diluentVol / maxAspirateVolume.value);
            const isOverFlowTip = diluentVol > maxAspirateVolume.value;
            const volume = isOverFlowTip ? maxAspirateVolume.value : diluentVol;

            for (let index = 0; index < cycle; index++) {
              dispatch(
                cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                  ID: newTaskID++,
                  type: TaskType.PICK_TIP,
                  columnTipCount: 1,
                  rowTipCount: 1,
                  row: '',
                  column: '',
                  number: number++,
                  labwareID: 2,
                }),
              );
              dispatch(
                cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                  ID: newTaskID++,
                  type: TaskType.ASPIRATE,
                  columnTipCount: 1,
                  rowTipCount: 1,
                  row: 'A',
                  column: 1,
                  volume,
                  number: number++,
                  labwareID: selectedDiluentLabwareID,
                  tipPosition: minTipPositionDiluentLabware,
                  mixingPosition: minTipPositionDiluentLabware,
                  speed: 3,
                  retractSpeed: 20,
                  enableMixing: false,
                  mixingSpeed: 3,
                }),
              );
              dispatch(
                cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                  ID: newTaskID++,
                  type: TaskType.DISPENSE,
                  row: String.fromCharCode(64 + row + 1),
                  columnTipCount: 1,
                  rowTipCount: 1,
                  column: col + 1,
                  number: number++,
                  volume,
                  labwareID: protocolEditorState.selectedLabwareID,
                  tipPosition: minTipPositionTargetLabware,
                  mixingPosition: minTipPositionTargetLabware,
                  speed: 3,
                  retractSpeed: 20,
                  enableMixing: false,
                  mixingSpeed: 3,
                }),
              );
              dispatch(
                cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                  ID: newTaskID++,
                  type: TaskType.EJECT_TIP,
                  number: number++,
                  columnTipCount: 1,
                  rowTipCount: 1,
                }),
              );
            }
            if (isOverFlowTip) {
              const leftOverVolume = diluentVol % maxAspirateVolume.value;
              if (leftOverVolume > 0) {
                dispatch(
                  cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                    ID: newTaskID++,
                    type: TaskType.PICK_TIP,
                    columnTipCount: 1,
                    rowTipCount: 1,
                    row: '',
                    column: '',
                    number: number++,
                    labwareID: 2,
                  }),
                );
                dispatch(
                  cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                    ID: newTaskID++,
                    type: TaskType.ASPIRATE,
                    columnTipCount: 1,
                    rowTipCount: 1,
                    row: 'A',
                    column: 1,
                    volume: leftOverVolume,
                    number: number++,
                    labwareID: selectedDiluentLabwareID,
                    tipPosition: minTipPositionDiluentLabware,
                    mixingPosition: minTipPositionDiluentLabware,
                    speed: 3,
                    retractSpeed: 20,
                    enableMixing: false,
                    mixingSpeed: 3,
                  }),
                );
                dispatch(
                  cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                    ID: newTaskID++,
                    type: TaskType.DISPENSE,
                    row: String.fromCharCode(64 + row + 1),
                    columnTipCount: 1,
                    rowTipCount: 1,
                    column: col + 1,
                    number: number++,
                    volume: leftOverVolume,
                    labwareID: protocolEditorState.selectedLabwareID,
                    tipPosition: minTipPositionTargetLabware,
                    mixingPosition: minTipPositionTargetLabware,
                    speed: 3,
                    retractSpeed: 20,
                    enableMixing: false,
                    mixingSpeed: 3,
                  }),
                );
                dispatch(
                  cardStateAction.addCardTask(cardID, cardName, CardType.NORMALIZATION, newTaskID, {
                    ID: newTaskID++,
                    type: TaskType.EJECT_TIP,
                    number: number++,
                    columnTipCount: 1,
                    rowTipCount: 1,
                  }),
                );
              }
            }
          }
        }

        dispatch(cardStateAction.saveNormalizationStateToCard(cardID, normalizationState));
        dispatch(cardStateAction.setCardIsSelected(cardID));
        dispatch(normalizationStateAction.resetNormalizationState());

        if (isEditing) {
          dispatch(temporaryStateAction.removeTemporaryState(TemporaryStateType.CARDS));
          dispatch(temporaryStateAction.removeTemporaryState(TemporaryStateType.TASKS));
        }
        dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardID));
      });
    });
  };
}
// #endregion Normalization Old UI

// #region duplicate card
const synchronizeCardIdInsideGroup = (groups, cards) => {
  const newGroup = jsonCopy(groups);
  Object.keys(groups).forEach((groupID) => {
    // eslint-disable-next-line eqeqeq
    const cardOfGroup = Object.values(cards).filter((card) => card.groupID == groupID);
    const cardIds = cardOfGroup.map((c) => c.ID);
    newGroup[groupID] = {...groups[groupID], cards: cardIds};
  });
  return newGroup;
};

const synchronizeCardGroup = (groups, cards) => {
  const newCard = jsonCopy(cards);
  Object.keys(groups).forEach((groupID) => {
    // eslint-disable-next-line eqeqeq
    const cardOfGroup = Object.values(cards).filter((card) => groups[groupID].cards.includes(card.ID));
    cardOfGroup.forEach((c) => {
      newCard[c.ID] = {...cards[c.ID], groupID: parseInt(groupID, 10)};
    });
  });
  return newCard;
};

const rearrangeAfterDuplicateCardsAndGroup = (newInsertedCards, newInsertedGroups) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, groups} = cardState;
  let newCards = jsonCopy(cards);
  let newGroups = jsonCopy(groups);
  let idAfterRearrange = [];

  if (!isEmpty(newInsertedCards)) {
    Object.keys(newInsertedCards).forEach((key) => {
      if (Array.isArray(newInsertedCards[key])) {
        const newCardList = [];
        newInsertedCards[key].forEach((card) => {
          newCardList.push({...newCards[card.ID]});
          delete newCards[card.ID];
        });
        newInsertedCards[key] = newCardList;
      } else {
        newInsertedCards[key] = {...newCards[newInsertedCards[key].ID]};
        delete newCards[newInsertedCards[key].ID];
      }
    });

    [newCards, idAfterRearrange] = bulkInsertAfterCertainID(newInsertedCards, newCards, true);
    newGroups = synchronizeCardIdInsideGroup(newGroups, newCards);
  }

  if (!isEmpty(newInsertedGroups)) {
    Object.keys(newInsertedGroups).forEach((key) => {
      newInsertedGroups[key] = {...newGroups[newInsertedGroups[key].ID]};
      delete newGroups[newInsertedGroups[key].ID];
    });

    newGroups = bulkInsertAfterCertainID(newInsertedGroups, newGroups);
    newCards = synchronizeCardGroup(newGroups, newCards);
  }

  batch(async () => {
    await dispatch(cardStateAction.restoreCardState(newCards));
    await dispatch(cardStateAction.restoreGroupState(newGroups));
    await dispatch(smarterDefaultDispenseTipPosition());
  });
  return idAfterRearrange;
};

const duplicateTasks = (taskIds, startingId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {tasks} = cardState;

  let taskList = filterToArray(tasks, {ID: taskIds});
  taskList = orderBy(taskList, ['number'], ['asc']);
  let taskId = startingId;

  const taskIdList = [];
  const newTaskObject = {};

  const newTaskList = taskList.map((task) => {
    const newTaskId = taskId;
    const newTask = {...task, ID: newTaskId};

    taskId += 1;
    taskIdList.push(newTaskId);
    newTaskObject[newTaskId] = newTask;

    return newTask;
  });

  return {taskIdList, newTaskList, newTaskObject};
};

// TO-DO DH: refactor this function so it will call specific card duplication functions per card type instead of general function
// such as standardizeSdRawTasks
const adjustCardDuplicationByType = (card, tasksState) => {
  const {type: cardType} = card || {};

  switch (cardType) {
    case CardType.SERIAL_DILUTION:
      return standardizeSdRawTasks(card, tasksState)?.alteredCard;
    default:
      return card;
  }
};

export const duplicateCard = (card) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  if (!card) {
    return null;
  }

  const generatedIds = generateCardIDAndTaskIDAndNumber(
    cards,
    tasks,
    CardType.SIMPLE_TRANSFER,
    undefined,
    TaskType.ASPIRATE,
    null,
    {row: null, column: null},
  );

  const cardNames = [];

  Object.keys(cards).forEach((e) => {
    const cardName = cards[e].name;
    if (cardName) {
      cardNames.push(cardName);
    }
  });

  const newValidName = incrementName(card.name, cardNames);
  const [newCardId] = generatedIds;

  let newCard = {
    ...card,
    ID: newCardId,
    name: newValidName,
    tasks: [],
  };

  return batch(() => {
    const {taskIdList, newTaskObject} = dispatch(duplicateTasks(card.tasks, generatedIds[1]));

    newCard = {...newCard, tasks: taskIdList};
    newCard = adjustCardDuplicationByType(newCard, newTaskObject);

    dispatch(cardStateAction.setCardIsSelected(card.ID, false, false));
    dispatch(cardStateAction.setCardIsExpanded(card.ID, false));

    dispatch(cardStateAction.addCardByObject(newCardId, newCard));
    dispatch(cardStateAction.batchAddTasks(newTaskObject));

    // if original card is contained inside a group, add the new card on group's cards list
    if (typeof card.groupID === 'number') {
      dispatch(cardStateAction.addCardIdsToGroupCardList(card.groupID, [newCardId]));
    }

    return newCard;
  });
};

export const duplicateCards =
  (cardIdList, cardObjectList, rearrange = true, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    let cardList = [];
    if (cardObjectList?.length) {
      cardList = cardObjectList;
    } else {
      const cardIdToDuplicate = cardIdList;

      if (cardIdToDuplicate) {
        cardList = filterToArray(cards, {ID: cardIdList});
      } else {
        cardList = Object.values(cards).filter((c) => c.isSelected);
      }
    }

    let newInsertedCardIds = [];
    const newInsertedCards = {};

    batch(async () => {
      cardList.forEach((card) => {
        const newCard = dispatch(duplicateCard(card));
        if (newCard) {
          newInsertedCards[card.ID] = newCard;
          newInsertedCardIds.push(newCard.ID);
        }
      });

      if (rearrange) {
        newInsertedCardIds = dispatch(rearrangeAfterDuplicateCardsAndGroup(newInsertedCards));
      }
      if (!skipSaveState) {
        dispatch(globalAddUndoItem(cardState));
      }
    });
    return [newInsertedCardIds, Object.values(newInsertedCards)];
  };

const duplicateGroup = (group) => (dispatch, getState) => {
  const {cardState} = getState();
  const {groups} = cardState;

  let newGroup;
  let newValidName = group.name;
  let newCards = {};
  const groupNames = [];

  Object.keys(groups).forEach((e) => {
    const cardName = groups[e].name;
    if (cardName) {
      groupNames.push(cardName);
    }
  });

  newValidName = incrementName(group.name, groupNames);

  batch(async () => {
    const duplicateResult = dispatch(duplicateCards(group.cards, null, false, true));
    const [newCardIds] = duplicateResult;
    [, newCards] = duplicateResult;
    newGroup = dispatch(addCardGroup(newCardIds, newCardIds[0], null, true, true, null, false));

    await dispatch(cardStateAction.setGroupIsSelected(group.ID, false, false));
    await dispatch(cardStateAction.setCardIsSelected(newCardIds, false));

    await dispatch(cardStateAction.setCardGroupName(newGroup.ID, newValidName));
    await dispatch(cardStateAction.setGroupIsSelected(newGroup.ID, false, true));
  });

  return [{...newGroup, name: newValidName}, newCards];
};

export const duplicateCardsAndGroups = (groupIdList, cardIdList) => (dispatch, getState) => {
  const generateGroup = (group) =>
    new Promise((resolve) => {
      (async () => {
        const cardLength = group.cards.length;
        const lastCardId = group.cards[cardLength - 1];
        const retval = await dispatch(duplicateGroup(group, null, false));
        resolve([...retval, lastCardId, group.ID]);
      })();
    });

  const generateCard = (cardsToDuplicate) =>
    new Promise((resolve) => {
      (async () => {
        const [newInsertedCardIds] = await dispatch(duplicateCards([], cardsToDuplicate, false, true));
        resolve(newInsertedCardIds);
      })();
    });

  const {cardState} = getState();
  const {groups, cards} = cardState;

  const oldCardState = jsonCopy(cardState);

  const groupIdToDuplicate = groupIdList;
  let groupList = [];
  if (groupIdToDuplicate) {
    groupList = filterToArray(groups, {ID: groupIdList});
  } else {
    groupList = Object.values(groups).filter((g) => g.isSelected);
  }

  const cardIdToDuplicate = cardIdList;
  let cardList = [];
  if (cardIdToDuplicate) {
    cardList = filterToArray(cards, {ID: cardIdList});
  } else {
    cardList = Object.values(cards).filter((c) => c.isSelected);
  }

  if (!groupList.length && !cardList.length) {
    return;
  }

  batch(async () => {
    const groupDuplicator = [];
    let cardIdsInsideGroup = [];
    groupList.forEach((group) => {
      cardIdsInsideGroup = [...cardIdsInsideGroup, ...group.cards];
    });
    const cardsToDuplicate = [];
    cardList.forEach((card) => {
      if (!cardIdsInsideGroup.includes(card.ID)) {
        cardsToDuplicate.push(card);
      }
    });

    generateCard(cardsToDuplicate).then((newInsertedCardIds) => {
      groupList.forEach((group) => {
        groupDuplicator.push(generateGroup(group));
      });
      Promise.all(groupDuplicator).then(async (results) => {
        dispatch(cardStateAction.setCardIsSelected(newInsertedCardIds, false));
        const newInsertedCards = {};
        const newInsertedGroups = {};
        results.forEach((data) => {
          const [newGroup, newCards, lastCardId, groupID] = data;
          if (!isEmpty(newGroup)) {
            newInsertedCards[lastCardId] = [...newCards];
            newInsertedGroups[groupID] = newGroup;
          }
        });

        await dispatch(rearrangeAfterDuplicateCardsAndGroup(newInsertedCards, newInsertedGroups));
        dispatch(globalAddUndoItem(oldCardState));
      });
    });
  });
};
// #endregion duplicate card

// #region fast preparation wizard

export const generateSetupWizardProtocol = (callback) => (dispatch, getState) => {
  // region labware
  const {availableLabwareState} = getState();
  const {1: aspirateLabware, 2: dispenseLabware} = jsonCopy(availableLabwareState);

  aspirateLabware.LabwareID = `${aspirateLabware.IDPrefix}${String(0).padStart(2, '0')}`;
  dispenseLabware.LabwareID = `${dispenseLabware.IDPrefix}${String(0).padStart(2, '0')}`;

  const newLabwareState = {
    1: aspirateLabware,
    2: {},
    3: {},
    4: dispenseLabware,
    5: {},
    6: {},
  };

  // region tasks
  const taskList = [];
  let taskId = 0;
  let number = 0;
  const aspirateVolume = 10;
  const dispenseVolume = 2.5;
  const columnTipCount = 12;
  const rowTipCount = 8;

  const task = {
    rowTipCount,
    columnTipCount,
    enableMixing: false,
    mixingCycle: 3,
    tipPosition: 1,
    mixingPositionAspirate: 1,
    mixingPositionDispense: 1,
    speed: 3,
    retractSpeed: 20,
    mixingSpeed: 3,
    isIgnored: false,
  };

  const dispenseRowColumn = [
    {row: 'A', column: 1},
    {row: 'B', column: 1},
    {row: 'A', column: 2},
    {row: 'B', column: 2},
  ];

  // add pick tip task
  taskList.push({
    ...task,
    ID: taskId++,
    type: TaskType.PICK_TIP,
    row: '',
    column: '',
    number: number++,
    labwareID: 2,
  });
  // add aspirate task
  taskList.push({
    ...task,
    ID: taskId++,
    type: TaskType.ASPIRATE,
    row: 'A',
    column: 1,
    number: number++,
    labwareID: aspirateLabware.LabwareID,
    volume: aspirateVolume,
    mixingVolume: aspirateVolume,
  });
  // add dispense task
  dispenseRowColumn.forEach((rowColumn) => {
    taskList.push({
      ...task,
      ID: taskId++,
      type: TaskType.DISPENSE,
      row: rowColumn.row,
      column: rowColumn.column,
      number: number++,
      labwareID: dispenseLabware.LabwareID,
      volume: dispenseVolume,
      mixingVolume: dispenseVolume,
    });
  });
  // add eject tip task
  taskList.push({
    ...task,
    ID: taskId++,
    type: TaskType.EJECT_TIP,
    row: '',
    column: '',
    number: number++,
    labwareID: 2,
  });

  // region card
  const cardName = 'Simple Transfer';
  const cardId = 0;

  batch(() => {
    dispatch(cardStateAction.resetCardState());
    dispatch(labwareStateAction.restoreLabwareState(newLabwareState));
    dispatch(cardStateAction.addCard(cardId, cardName, CardType.SIMPLE_TRANSFER));
    dispatch(cardStateAction.batchAddCardTask(cardId, cardName, CardType.SIMPLE_TRANSFER, taskList));

    const {cardState, labwareState} = getState();
    callback(cardState, labwareState);
  });
};
// #endregion fast preparation wizard

export const getCopyPipettingValueMode = (taskSource) => (dispatch, getState) => {
  const {configState, cardState, labwareState} = getState();

  const {cards: cardStateCards, tasks: cardStateTasks} = cardState;
  const {deepWellThreshold} = configState;

  const labwareUsed = find(labwareState, {LabwareID: taskSource.labwareID});

  // ignore tasks in deep well plate
  if (!labwareUsed || labwareUsed?.wellDepth > deepWellThreshold) {
    return CopyPipettingValueMode.NONE;
  }

  const sameTaskTypeInSameCard = [];

  // get required card
  const cards = filterToArray(cardStateCards, (e) => e.type === CardType.SIMPLE_TRANSFER);
  const selectedCard = find(cards, {isSelected: true});

  // no card selected. skip checking
  if (!selectedCard) return CopyPipettingValueMode.NONE;

  const tasks = [];

  cards.forEach((card) => {
    const tasksInCard = getTasksInCard(card, cardStateTasks, {isReturnArray: true});

    const tasksInCardWithoutDeepWell = tasksInCard.filter((task) => {
      if (task.type !== taskSource.type) return false;

      const taskLabware = find(labwareState, {LabwareID: task.labwareID});
      const isUsingDeepWell = taskLabware?.wellDepth > deepWellThreshold;

      if (!isUsingDeepWell && card.ID === selectedCard.ID) {
        sameTaskTypeInSameCard.push(task);
      }

      return !isUsingDeepWell;
    });

    tasks.push(...tasksInCardWithoutDeepWell);
  });

  if (!tasks || !cards || !selectedCard) {
    return CopyPipettingValueMode.NONE;
  }

  // single task
  if (tasks.length === 1) {
    return CopyPipettingValueMode.NONE;
  }

  // multiple tasks, single card
  if (cards.length === 1) {
    return CopyPipettingValueMode.TO_ALL_TASKS;
  }

  const cardMap = {};
  let multipleCards = false;

  tasks.every((t) => {
    cards.every((c) => {
      if (c.tasks.indexOf(t.ID) >= 0) {
        cardMap[c.ID] = true;

        return false;
      }

      return true;
    });

    if (Object.keys(cardMap).length > 1) {
      multipleCards = true;

      return false;
    }

    return true;
  });

  // multiple tasks, multiple cards
  if (multipleCards) {
    if (sameTaskTypeInSameCard.length > 1) return CopyPipettingValueMode.BOTH;

    return CopyPipettingValueMode.TO_ALL_CARDS;
  }

  return CopyPipettingValueMode.TO_ALL_TASKS;
};

export function checkAndApplyGeneralTask(task, card) {
  return async (dispatch, getState) => {
    const {virtualTrayState} = getState();
    const {shownModal} = virtualTrayState;

    const {ID: cardId, isInsertTask, type: cardType} = card;

    let isValid = false;

    // check for validity
    switch (task.type) {
      case TaskType.DELAY:
        isValid = parseFloat(task.delayTime) !== 0;
        break;
      case TaskType.USER_CONFIRMATION:
      case TaskType.USER_CONFIRMATION_EJECT:
        isValid = task.message && !shownModal;
        break;
      case TaskType.AIR_GAP:
        isValid = task.volume && !shownModal;
        break;
      default:
        break;
    }

    if (isValid) {
      dispatch(editGeneralTask(cardId, task));
      if (isInsertTask) {
        const insertTaskSuccessMsg = parseMessage('protocolEditor.taskList.insertTask.notification.success');
        dispatch(appStateAction.showSnackbar(Feedback.SUCCESS, insertTaskSuccessMsg));
      }
      dispatch(protocolManagementBusiness.restoreTipCountForSingleCard(cardId));
    } else {
      // eslint-disable-next-line no-lonely-if
      if (cardType !== CardType.SIMPLE_TRANSFER) {
        dispatch(deleteCard(cardId));
      } else {
        dispatch(removeCardTask(cardId, task.ID));
      }
    }
  };
}

export const getTasksInFirstSelectedCard = () => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = getFirstSelectedCard(cards);
  return getTasksInCard(selectedCard, tasks);
};

export const setMultipleTaskIsSelected = (taskIds, isSelected) => (dispatch, getState) => {
  const {cardState} = getState();
  const {tasks} = cardState;

  const updatedTasks = [];

  taskIds.forEach((id) => {
    updatedTasks.push({...tasks[id], isSelected});
  });

  dispatch(cardStateAction.batchRestoreCardTaskStateById(updatedTasks));
};

export const setMultipleTaskStateSelected = (tasks, cardID) => (dispatch) => {
  const groupedTasks = groupTaskByNumber(tasks);

  const mappedTasks = mapObject(groupedTasks, (tasksByNumber, taskNumber) => ({
    [taskNumber]: tasksByNumber.map((task) => ({
      taskID: task.ID,
      taskNumber: task.number,
      cardID,
    })),
  }));

  dispatch(setMultipleSelectedTasks(mappedTasks));
};

export const isTaskNumberSelected = (taskNumber, cardID) => (dispatch, getState) => {
  if (!taskNumber) return false;

  const selectedTasks = getSelectedTasks(getState());
  const selectedTaskNumber = selectedTasks[taskNumber];

  return Boolean(selectedTaskNumber) && selectedTaskNumber.find((task) => task.cardID === cardID);
};

export const isTaskIDSelected = (taskID) => (dispatch, getState) => {
  if (isNil(taskID)) return false;

  const selectedTasks = getSelectedTasks(getState());
  return Object.values(selectedTasks).find((selectedTasksByNumber) =>
    selectedTasksByNumber.find((task) => task.ID === taskID),
  );
};

export const getGroupIndexByCardId = (cardId, newCardGroup) => (dispatch, getState) => {
  if (!newCardGroup.length || !cardId) {
    return [null, null];
  }
  const cardStateCards = getCards(getState());
  const card = cardStateCards[cardId];
  const cardGroupId = card.groupID;
  if (cardGroupId) {
    const groupIndex = newCardGroup.findIndex((g) => g.ID === cardGroupId);
    const currentCards = newCardGroup[groupIndex].cards;
    const cardIndex = currentCards.findIndex((c) => c.ID === cardId);
    return [groupIndex, cardIndex];
  }

  const groupIndex = newCardGroup.findIndex((g) => g.card && g.card?.ID === cardId);
  return [groupIndex, '#'];
};

export const updateCardTasksEject = (tipsWasteToCaddy) => (dispatch, getState) => {
  const {
    cardState: {cards, tasks},
  } = getState();
  const STCardTasks = [];
  Object.values(cards).forEach((ele) => {
    STCardTasks.push(...ele.tasks);
  });
  const ejectTasks = STCardTasks.filter((ele) => tasks[ele].type === TaskType.EJECT_TIP);
  const pickTipTasks = STCardTasks.filter(
    (ele) => tasks[ele].type === TaskType.PICK_TIP && tasks[ele].labwareID === TipCaddyLocationType.TIP_WASTE_CADDY,
  );
  batch(() => {
    dispatch(cardStateAction.batchSetTasksLocation(ejectTasks, tipsWasteToCaddy ? 1 : 0));
    dispatch(cardStateAction.batchSetTasksLocation(pickTipTasks, TipCaddyLocationType.TIP_PICK_CADDY));
  });
};

export const addEjectTipTask =
  (labwareID = 1) =>
  async (dispatch, getState) => {
    const {protocolEditorState, cardState} = getState();
    const selectedCard = find(cardState.cards, {isSelected: true});

    dispatch(
      addSimpleTransferTask(
        TaskType.EJECT_TIP,
        protocolEditorState.selectedTipChannel,
        labwareID,
        '',
        '',
        0,
        selectedCard.ID,
        protocolEditorState.selectedTipChannel === TipChannelType.TIP_CHANNEL_CUSTOM
          ? protocolEditorState.customTipCount
          : null,
      ),
    );
  };

export const showEjectTipsSwitchModal = (tipsWasteToCaddy) => (dispatch) => {
  const modalHeader = intl.formatMessage({
    id: tipsWasteToCaddy
      ? 'modal.protocolEditor.tipsWasteTo.caddy.title'
      : 'modal.protocolEditor.tipsWasteTo.wasteBin.title',
  });
  const modalBody = intl.formatMessage({
    id: tipsWasteToCaddy
      ? 'modal.protocolEditor.tipsWasteTo.caddy.body'
      : 'modal.protocolEditor.tipsWasteTo.wasteBin.body',
  });
  const modalButtons = [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: async () => {
        dispatch(hideModal());
        dispatch(updateCardTasksEject(tipsWasteToCaddy));
        dispatch(setOpenedProtocolEjectToCaddy(tipsWasteToCaddy));
        await dispatch(protocolManagementBusiness.saveProtocol());
        dispatch(saveState());
      },
      text: intl.formatMessage({
        id: CommonButtonLabel.CONFIRM,
      }),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: () => {
        dispatch(hideModal());
      },
      text: intl.formatMessage({
        id: CommonButtonLabel.CANCEL,
      }),
    },
  ];
  dispatch(
    showModal(
      modalHeader,
      modalBody,
      modalButtons,
      () => {},
      'changeAllEjectTipLocationConfirmation-modal',
      true,
      () => dispatch(hideModal()),
    ),
  );
};

export const isProtocolHasTaskForChangePickEjectLocationUpdate = () => (dispatch, getState) => {
  const {
    cardState: {tasks},
  } = getState();

  if (isEmpty(tasks)) {
    return false;
  }
  const task = Object.values(tasks).find(
    (ele) =>
      ele.type === TaskType.EJECT_TIP ||
      (ele.type === TaskType.PICK_TIP && ele.labwareID === TipCaddyLocationType.TIP_WASTE_CADDY),
  );
  if (!task) {
    return false;
  }
  return true;
};
